import React, { useEffect, useState, useRef, useCallback } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import api from "../services/api";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.04 } },
};
const itemVariants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0 } };

const emptyTransport = { fullName: "", monthlySalary: "", notes: "" };

export default function TransportPage() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(8);
  const [totalPages, setTotalPages] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(emptyTransport);
  const [editingId, setEditingId] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
  });

  const mountedRef = useRef(true);
  const debounceRef = useRef(null);

  // LOAD
  useEffect(() => {
    mountedRef.current = true;
    fetchData(1, query);
    return () => (mountedRef.current = false);
  }, []);

  const fetchData = useCallback(
    async (p = 1, q = "") => {
      setLoading(true);
      try {
        const params = { page: p, limit: pageSize };
        if (q.trim()) params.search = q.trim();

        const { data } = await api.get("/transport", { params });

        if (!mountedRef.current) return;

        const list = data.items || [];
        const total = data.total || list.length;
        const pages = data.totalPages || Math.max(1, Math.ceil(total / pageSize));

        setItems(list);
        setPage(data.page || p);
        setTotalPages(pages);
      } catch (err) {
        toast.error("فشل في تحميل بيانات النقل");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [pageSize]
  );

  // Search debounce
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(1, query), 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchData]);

  // Add or Edit
  const openAdd = () => {
    setForm(emptyTransport);
    setIsEdit(false);
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      fullName: row.fullName,
      monthlySalary: row.monthlySalary,
      notes: row.notes,
    });
    setIsEdit(true);
    setEditingId(row._id);
    setModalOpen(true);
  };

  const saveItem = async () => {
    const name = form.fullName.trim();
    if (name.length < 3) return toast.error("الرجاء إدخال اسم صالح");

    const salary = Number(form.monthlySalary);
    if (isNaN(salary) || salary <= 0)
      return toast.error("الراتب الشهري يجب أن يكون رقمًا أكبر من صفر");

    const payload = {
      fullName: name,
      monthlySalary: salary,
      notes: form.notes || "",
    };

    try {
      if (isEdit) {
        await api.put(`/transport/${editingId}`, payload);
        toast.success("تم تحديث السجل");
      } else {
        await api.post("/transport", payload);
        toast.success("تم إضافة عنصر نقل");
      }

      setModalOpen(false);
      fetchData(page, query);
    } catch (err) {
      toast.error("فشل في حفظ البيانات");
    }
  };

  // Delete
  const doDelete = async () => {
    try {
      await api.delete(`/transport/${confirmDelete.id}`);
      toast.success("تم الحذف بنجاح");
      setConfirmDelete({ open: false, id: null, name: "" });
      fetchData(1, query);
    } catch (err) {
      toast.error("فشل في حذف السجل");
    }
  };

  // Salary total
  const totalSalary = items.reduce((sum, t) => sum + Number(t.monthlySalary || 0), 0);

  return (
    <div dir="rtl" className="min-h-screen p-6 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-1000">إدارة النقل</h1>
            <p className="text-slate-800 mt-1">إضافة وتعديل وحذف وبحث سريع  .</p>
          </div>

          <div className="flex gap-3 items-center">
            <div className="relative flex items-center">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ابحث بالاسم..."
                className="pl-10 pr-3"
              />
              <span className="absolute left-3 top-3 text-slate-400">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
            </div>

            <Button onClick={openAdd} className="flex items-center gap-2">
              <PlusCircleIcon className="h-5 w-5" />
              إضافة
            </Button>
          </div>
        </div>

        {/* CARDS */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">

            {/* Count */}
            <Card className="p-4">
              <div className="text-lg font-bold text-slate-900">عدد السائقين :</div>
              <div className="text-2xl text-green-700">{items.length}</div>
            </Card>

            {/* Salary Total */}
            <Card className="p-4">
              <div className="text-lg font-bold text-slate-900">إجمالي الرواتب :</div>
              <div className="text-2xl  text-green-700">
                {totalSalary.toLocaleString()} ل.س
              </div>
            </Card>

       

          </motion.div>

          {/* TABLE */}
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200"
          >
            <div className="p-4 border-b bg-slate-50">
              <div className="text-lg font-semibold">قائمة السائقين</div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-right">
                <thead className="bg-white">
                  <tr>
                    <th className="p-4 text-lg font-bold">الاسم الكامل</th>
                    <th className="p-4 text-lg font-bold">الراتب الشهري</th>
                    <th className="p-4 text-lg font-bold">ملاحظات</th>
                    <th className="p-4 text-lg font-bold">الإجراءات</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {items.map((row) => (
                    <tr key={row._id} className="hover:bg-slate-50 transition font-semibold text-slate-800 whitespace-nowrap">
                      <td className="p-4">{row.fullName}</td>
                      <td className="p-4 text-green-700 font-semibold">
                        {Number(row.monthlySalary).toLocaleString()} ل.س
                      </td>
                      <td className="p-4 max-w-xs truncate">{row.notes || "-"}</td>

                      <td className="p-4 flex gap-2">
                        <Button
                          onClick={() => openEdit(row)}
                          variant="ghost"
                          className="!px-3 !py-2"
                          title="تعديل"

                        >
                          <PencilSquareIcon className="h-6 w-6 text-blue-500" />
                        </Button>

                        <Button
                          onClick={() => setConfirmDelete({ open: true, id: row._id, name: row.fullName })}
                          variant="ghost"
                          className="!px-3 !py-2"
                          title="حذف"

                        >
                          <TrashIcon className="h-6 w-6 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}

                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-12 text-center text-slate-500">
                        لا توجد بيانات
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* MODAL ADD / EDIT */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/40" onClick={() => setModalOpen(false)} />

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 10, opacity: 0 }}
              className="relative z-10 w-full max-w-xl"
            >
              <Card className="p-6">

                <div className="flex items-start justify-between">
                  <h2 className="text-2xl font-bold">
                    {isEdit ? "تعديل بيانات السائق" : "إضافة سائق"}
                  </h2>

                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setModalOpen(false)}>
                      إلغاء
                    </Button>
                    <Button onClick={saveItem}>
                      {isEdit ? "حفظ" : "إضافة"}
                    </Button>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="الاسم الكامل"
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  />

                  <Input
                    label="الراتب الشهري (ل.س)"
                    type="number"
                    value={form.monthlySalary}
                    onChange={(e) => setForm({ ...form, monthlySalary: e.target.value })}
                  />

                  <Input
                    label="ملاحظات"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>

              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DELETE CONFIRM */}
      <AnimatePresence>
        {confirmDelete.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/40"
              onClick={() => setConfirmDelete({ open: false, id: null, name: "" })}
            />

            <motion.div initial={{ y: 15, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }}
              className="relative z-10 w-full max-w-md"
            >
              <Card className="p-6">
                <h3 className="text-xl font-bold">تأكيد الحذف</h3>
                <p className="text-slate-700 mt-2">
                  هل تريد حذف <span className="font-bold">{confirmDelete.name}</span>؟
                </p>

                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="ghost"
                    onClick={() => setConfirmDelete({ open: false, id: null, name: "" })}
                  >
                    إلغاء
                  </Button>

                  <Button variant="danger" onClick={doDelete}>
                    حذف نهائي
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
